import type { TrialBalanceEntry } from '../types/filters';

/**
 * Interface pour l'entrée de balance telle que retournée par l'API
 */
export interface ApiTrialBalanceEntry {
  accountId?: string;
  id?: string;
  accountCode?: string;
  code?: string;
  accountName?: string;
  name?: string;
  accountType?: string;
  type?: string;
  debit?: number;
  credit?: number;
  balance?: number;
  class?: string;
}

/**
 * Mappe une entrée de balance de l'API vers le format interne du frontend
 * 
 * L'API retourne:
 * { accountId, accountCode, accountName, accountType, debit, credit, balance: number, class }
 * 
 * Le frontend attend:
 * { account: { id, code, name, type, class }, debit, credit, balance: { raw, debitBalance, creditBalance, normalized } }
 */
export function mapApiTrialBalanceEntry(apiEntry: ApiTrialBalanceEntry): TrialBalanceEntry {
  const debit = apiEntry.debit || 0;
  const credit = apiEntry.credit || 0;
  const rawBalance = typeof apiEntry.balance === 'number' 
    ? apiEntry.balance 
    : (debit - credit);
  
  const code = apiEntry.accountCode || apiEntry.code || '';
  
  return {
    account: {
      id: apiEntry.accountId || apiEntry.id || '',
      code: code,
      name: apiEntry.accountName || apiEntry.name || '',
      type: (apiEntry.accountType || apiEntry.type || 'asset') as 'asset' | 'liability' | 'equity' | 'revenue' | 'expense',
      class: apiEntry.class || code.charAt(0) || ''
    },
    debit,
    credit,
    balance: {
      raw: rawBalance,
      debitBalance: rawBalance > 0 ? rawBalance : 0,
      creditBalance: rawBalance < 0 ? Math.abs(rawBalance) : 0,
      normalized: rawBalance
    }
  };
}

/**
 * Mappe un tableau d'entrées de balance de l'API vers le format frontend
 */
export function mapApiTrialBalanceEntries(apiEntries: ApiTrialBalanceEntry[] | unknown): TrialBalanceEntry[] {
  if (!Array.isArray(apiEntries)) {
    return [];
  }
  return apiEntries.map(mapApiTrialBalanceEntry);
}

/**
 * Extrait et mappe les entrées d'une réponse API de balance
 * Gère les deux formats possibles: response.data.entries ou response.data.items
 */
export function extractAndMapTrialBalanceEntries(responseData: any): TrialBalanceEntry[] {
  if (!responseData) return [];
  
  const rawEntries = responseData.entries || responseData.items || [];
  return mapApiTrialBalanceEntries(rawEntries);
}
